import React, { useCallback } from 'react';
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  useTheme,
  Link,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import { useItemAttributes, useItemRendering } from 'hooks';
import { IItem, IItemKey } from 'api/catalog/models';
import NoInformationAvailable from 'ui/ErrorDisplay/NoInformationAvailable';
import { ITEM_AVAILABILITY_COLUMNS } from 'constants/table';
import { ComputedRoute } from 'utils/ComputedRoute';

interface IProps {
  item: IItem;
}

const AvailabilityRow = ({ item }: IProps) => {
  const { getCommonAttributeRender } = useItemRendering();
  const renderCell = useCallback(
    (key: IItemKey): React.ReactNode => {
      switch (key) {
        case 'itemNumber':
          return (
            <Link
              component={RouterLink}
              to={ComputedRoute.getProductDetailsRoute(item.elasticId)}
            >
              {item.itemNumber}
            </Link>
          );
        default:
          return getCommonAttributeRender(item, key);
      }
    },
    [getCommonAttributeRender, item]
  );

  return (
    <TableRow hover>
      {ITEM_AVAILABILITY_COLUMNS.map((key) => (
        <TableCell key={key}>{renderCell(key)}</TableCell>
      ))}
    </TableRow>
  );
};

const ItemAvailability = ({ item }: IProps) => {
  const { getLabel } = useItemAttributes();
  const theme = useTheme();

  return item ? (
    <TableContainer>
      <Table size='small'>
        <TableHead>
          <TableRow>
            {ITEM_AVAILABILITY_COLUMNS.map((key) => (
              <TableCell
                key={key}
                sx={{
                  backgroundColor: theme.palette.grey[200],
                  fontWeight: 'bold',
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                {getLabel(key)}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {item.itemAtOtherDcs
            ? item.itemAtOtherDcs.map((siblingItem) => (
                <AvailabilityRow
                  key={siblingItem.elasticId}
                  item={siblingItem}
                />
              ))
            : null}
        </TableBody>
      </Table>
    </TableContainer>
  ) : (
    <NoInformationAvailable minHeight='300px' />
  );
};

export default ItemAvailability;
